import { useAddSubuser } from '@/common/apis/services/auth/subuser/addSubuser';
import { useGetSubuser } from '@/common/apis/services/auth/subuser/getSubuser';
import Button from '@/common/components/atom/button';
import Modal from '@/common/components/atom/modal';
import Skeleton from '@/common/components/atom/skeleton';
import { ClinicStatus } from '@/common/constants/status/clinicStatus';
import useModal from '@/common/hooks/useModal';
import classNames from '@/common/utils/classNames';
import { hasDoctorIdentity } from '@/common/utils/doctorIdentity';
import { useLoginModalContext } from '@/modules/login/context/loginModal';
import { UserInfo, useUserInfoStore } from '@/modules/login/store/userInfo';
import { FormFields, PatinetProfileForm } from '@/modules/patient/views/form';
import orderBy from 'lodash/orderBy';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import UserCard from '../../components/userCard';
import { useRouter } from 'next/router';
import { isSelfCardGuardEnabled } from '../../utils/selfCardGuard';

interface SelectUserProps {
  onSelect: (user: any) => void;
  className?: string;
  shouldShowMessengers: boolean;
  loading?: boolean;
  onTrack?: (type: string, event?: Record<string, unknown>) => void;
}

export const SelectUser = (props: SelectUserProps) => {
  const { onSelect, className, shouldShowMessengers, loading, onTrack } = props;
  const userInfo = useUserInfoStore(state => state.info);
  const isLogin = useUserInfoStore(state => state.isLogin);
  const isUserPending = useUserInfoStore(state => state.pending);
  const { handleOpenLoginModal } = useLoginModalContext();
  const { data, mutate, isSuccess, isLoading, isIdle } = useGetSubuser();
  const addSubUser = useAddSubuser();
  const { handleOpen: handleOpenAddUserModal, handleClose: handleCloseAddUserModal, modalProps: addUserModalProps } = useModal();
  const [userSelected, setUserSelected] = useState(userInfo.id);
  const router = useRouter();
  const [selfCardGuard, setSelfCardGuard] = useState(false);
  useEffect(() => setSelfCardGuard(isSelfCardGuardEnabled()), []);

  useEffect(() => {
    if (!isLogin && !isUserPending) {
      setTimeout(
        () =>
          handleOpenLoginModal({
            state: true,
            onClose() {
              router.back();
              router.back();
            },
          }),
        0,
      );
    }
    if (isLogin && userInfo && !isUserPending) {
      mutate();
      setUserSelected(userInfo.id);
      handleSelectUser(userInfo);
    }

    return () => {
      handleOpenLoginModal({
        state: false,
      });
    };
  }, [isLogin, userInfo, isUserPending]);

  const handleOpenAddSubuserModal = () => {
    onTrack?.('subuser_add_open');
    addSubUser.reset();
    handleOpenAddUserModal();
  };

  const handleAddSubuser = async (data: any): Promise<boolean> => {
    const res = await addSubUser.mutateAsync({
      cell: data.cell,
      family: data.family,
      name: data.name,
      national_code: data.national_code,
      is_foreigner: data.is_foreigner ? '1' : '0',
      is_foreigner_web: data.is_foreigner,
      ...(data.gender && { gender: data.gender?.value }),
    });
    onTrack?.('subuser_add_submit', {
      result: res.data.status === ClinicStatus.SUCCESS ? 'success' : res.data.status === ClinicStatus.FORM_VALIDATION ? 'validation' : 'error',
    });
    if (res.data.status === ClinicStatus.SUCCESS) {
      mutate();
      handleCloseAddUserModal();
      handleSelectUser(res.data.result);
      return true;
    }
    if (res.data.status !== ClinicStatus.FORM_VALIDATION) toast.error(res.data.message);
    return false;
  };

  // Someone else's details typed on the account owner's own card: keep them as a family member.
  const handleSaveAsOtherPerson = async (data: any) => {
    const cell = userInfo.cell ? (userInfo.cell.startsWith('0') ? userInfo.cell : `0${userInfo.cell}`) : '';
    const saved = await handleAddSubuser({ ...data, cell });
    if (saved) toast.success(`«${data.name} ${data.family}» به افراد شما اضافه شد. اطلاعات حساب شما تغییر نکرد.`);
    else toast.error('ثبت نشد. این فرد را از «دریافت نوبت برای فرد دیگر» اضافه کنید.');
    return saved;
  };

  const handleSelectUser = (user: UserInfo, messengerType?: string) => {
    setUserSelected(user.id);
    onSelect({ ...user, ...(messengerType && { messengerType }) });
  };

  const getUserWithId = (id: string) => {
    return data?.data?.result?.find((item: any) => item.id === id) ?? userInfo;
  };

  return (
    <div className={classNames('flex flex-col space-y-6', className)}>
      <div className="flex flex-col w-full space-y-3">
        {(isLoading || isIdle) && <SubUserLoading />}
        {isSuccess && (
          <>
            <UserCard
              loading={loading}
              userId={userInfo.id ?? ''}
              name={userInfo.name ?? ''}
              family={userInfo.family ?? ''}
              cell={userInfo?.cell ? (userInfo.cell?.startsWith('0') ? userInfo.cell : `0${userInfo.cell}`) : undefined}
              email={userInfo.email}
              nationalCode={userInfo.national_code ?? ''}
              isForeigner={userInfo.is_foreigner ?? false}
              gender={userInfo.gender ?? ''}
              refetchData={mutate}
              onSelect={(id, payload) => handleSelectUser(getUserWithId(id), payload?.messengerType as string)}
              select={userInfo.id === userSelected}
              type="user"
              shouldShowMessengers={shouldShowMessengers}
              editable={!hasDoctorIdentity(userInfo)}
              onTrack={onTrack}
              onSaveAsOtherPerson={selfCardGuard ? handleSaveAsOtherPerson : undefined}
              onOtherPerson={selfCardGuard ? handleOpenAddSubuserModal : undefined}
              identityNotice={selfCardGuard}
            />
            {orderBy(data?.data?.result, 'created_at', 'desc')?.map((item: any) => (
              <UserCard
                loading={loading}
                key={item.id}
                userId={item.id}
                name={item.name}
                family={item.family}
                cell={item.cell}
                nationalCode={item.national_code}
                isForeigner={item.is_foreigner == '1'}
                gender={item.gender}
                refetchData={mutate}
                onSelect={(id, payload) => handleSelectUser(getUserWithId(id), payload?.messengerType as string)}
                select={item.id === userSelected}
                type="subUser"
                shouldShowMessengers={shouldShowMessengers}
                editable
                onTrack={onTrack}
              />
            ))}
          </>
        )}
      </div>
      <Button className="self-center" variant="secondary" onClick={handleOpenAddSubuserModal}>
        دریافت نوبت برای فرد دیگر
      </Button>
      <Modal name="select-user" title="کاربر جدید" {...addUserModalProps}>
        <PatinetProfileForm
          loading={addSubUser.isLoading}
          onSubmit={handleAddSubuser}
          fields={['NAME', 'FAMILY', 'GENDER', 'NATIONAL_CODE', 'CELL', 'IS_FOREIGNER'] as FormFields}
          errorsField={{ ...addSubUser.data?.data?.details }}
        />
      </Modal>
    </div>
  );
};

const SubUserLoading = () => {
  return (
    <div className="space-y-2">
      <Skeleton w="100%" h="5rem" rounded="lg" />
      <Skeleton w="100%" h="5rem" rounded="lg" />
      <Skeleton w="100%" h="5rem" rounded="lg" />
    </div>
  );
};

export default SelectUser;
